/*
  ЦИФРОВОЙ ДИАК ЗФ — APP ROUTER
  Design: «Арктический Командный Центр»
*/

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { SidebarLayoutProvider } from "./contexts/SidebarLayoutContext";
import Home from "./pages/Home";
import ProjectPage from "./pages/ProjectPage";
import ReviewPage from "./pages/ReviewPage";
import ReportsPage from "./pages/ReportsPage";
import MemosPage from "./pages/MemosPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminAuditPage from "./pages/AdminAuditPage";
import HistoryPage from "./pages/HistoryPage";
import MyTasksPage from "./pages/MyTasksPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

function AuthenticatedRouter() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/reports" component={ReportsPage} />
      <Route path="/memos" component={MemosPage} />
      <Route path="/review" component={ReviewPage} />
      <Route path="/history" component={HistoryPage} />
      <Route path="/me/tasks" component={MyTasksPage} />
      <Route path="/change-password" component={ChangePasswordPage} />
      <Route path="/admin/users" component={AdminUsersPage} />
      <Route path="/admin/audit" component={AdminAuditPage} />
      <Route path="/project/:id" component={ProjectPage} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function Gate() {
  const { user, loading, mustChangePassword } = useAuth();
  const [loc] = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Загрузка…
      </div>
    );
  }

  const isPublic = loc === "/login" || loc === "/register";
  if (!user && !isPublic) {
    return <Redirect to="/login" />;
  }
  if (user && isPublic) {
    return <Redirect to="/" />;
  }

  if (!user) {
    return (
      <Switch>
        <Route path="/login" component={LoginPage} />
        <Route path="/register" component={RegisterPage} />
        <Route component={() => <Redirect to="/login" />} />
      </Switch>
    );
  }

  // Если админ сбросил пароль — всегда показываем страницу смены пароля,
  // все остальные маршруты заблокированы до смены (сервер тоже отдаёт 423).
  if (mustChangePassword) {
    return <ChangePasswordPage />;
  }

  return (
    <ProjectProvider>
      <NotificationsProvider>
        <SidebarLayoutProvider>
          <TooltipProvider>
            <Toaster />
            <AuthenticatedRouter />
          </TooltipProvider>
        </SidebarLayoutProvider>
      </NotificationsProvider>
    </ProjectProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
